import type { SimEvent, SpillCause } from "../protocol/events";
import { CATEGORY_OF_NODE_KIND, type GridPos } from "../protocol/snapshot";
import { convertUnitToSludge, isBelowTargetPurity, totalUnits } from "./composition";
import { pollute } from "./pollution";
import {
  CATALYST_MIN_PURITY,
  CATALYST_POISON_PER_IMPURE,
  CONTAMINATION_HP_PER_TICK,
  OVERHEAT_TICKS_TO_EXPLOSION,
  PRESSURE_BURST_THRESHOLD,
  SLUDGE_RESIDENCE_TICKS,
  VERMIN_RISK_THRESHOLD,
  VERMIN_STAGNANT_TICKS,
} from "./definitions";
import {
  accrueVerminRisk,
  edgesTouching,
  facilityMitigationOf,
  heatShedOf,
  isTileContaminated,
  pressureToleranceOf,
  resistsCorrosion,
  resolveHoldRecipeForPacket,
  spendFacilityMitigation,
  verminLostTickPeriodOf,
  type EngineNode,
  type EngineState,
} from "./state";

/**
 * The five internal hazards of `03-hazards.md` §2, each implemented to the letter. They run
 * as two passes per tick: counters first (this file's `accumulate*`), then
 * failure resolution (`resolveFailures`), so that a node can't both cross a
 * threshold and explode from it in an order-dependent way.
 */

/**
 * 03-hazards.md §3.1 (spec 0005): marks a tile contaminated, exactly once.
 * Idempotent per exact tile position — a burst and a later leak landing on
 * the same waypoint mark it only the first time, so `pollute()` (keyed
 * per-tile, open question 8) never double-injects for one spot.
 */
export function contaminateTile(
  state: EngineState,
  pos: GridPos,
  cause: SpillCause,
  sourceId: string,
  events: SimEvent[],
): void {
  if (isTileContaminated(state, pos)) return;

  state.contaminatedTiles.push({ pos: { ...pos }, cause, sourceId, sinceTurn: state.turn });
  events.push({ type: "tile.contaminated", pos: { ...pos }, cause, sourceId });
  pollute(state, "spill", `${pos.x},${pos.y}`, events);

  // Spec 0028: Hazmat Absorbent answers the FIRST contamination of the stage
  // only (guarded by `spent`, so a later tile stands per Resolved Question
  // 1). This tile was only just pushed above, one call in step 6 — the
  // corrosion pass (`accumulateContamination`, step 5) that would charge
  // against it hasn't run yet this tick and never will, since the tile is
  // gone before the next tick's step 5 sees it.
  const absorbent = facilityMitigationOf(state, "hazmatAbsorbent");
  const absorbentSpent = state.installedFacilityMitigations.some((m) => m.mitigation.kind === "hazmatAbsorbent" && m.spent);
  if (absorbent && !absorbentSpent) {
    spendFacilityMitigation(state, "hazmatAbsorbent");
    state.contaminatedTiles = state.contaminatedTiles.filter((t) => !(t.pos.x === pos.x && t.pos.y === pos.y));
  }
}

/**
 * Chemical Hazard Spill (spec 0007): removes one tile's contamination mark.
 * Mirrors `contaminateTile`'s plain-array style; no event of its own — the
 * card's `hazard.cardCleared` narrates the clear.
 */
export function clearContamination(state: EngineState, pos: GridPos): void {
  state.contaminatedTiles = state.contaminatedTiles.filter(
    (t) => !(t.pos.x === pos.x && t.pos.y === pos.y),
  );
}

/** §2.1 Thermal — cooling first, then the warning/explosion clock. */
export function accumulateHeat(state: EngineState, events: SimEvent[]): void {
  const nodes = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const node of nodes) {
    if (node.isDestroyed) continue;

    const shed = heatShedOf(state, node);
    if (shed > 0 && node.heatCounter > 0) {
      node.heatCounter = Math.max(0, node.heatCounter - shed);
      events.push({
        type: "node.heated",
        nodeId: node.id,
        heatCounter: node.heatCounter,
        criticalTemp: node.criticalTemp,
      });
    }

    if (node.heatCounter >= node.criticalTemp) {
      node.isOverheatWarning = true;
      node.overheatTicks += 1;
      events.push({
        type: "node.overheatWarning",
        nodeId: node.id,
        ticksUntilExplosion: Math.max(0, OVERHEAT_TICKS_TO_EXPLOSION - node.overheatTicks),
      });
      // Thermal Shock is injected on the explosion itself (`resolveFailures`),
      // not the warning — a node cooled before it explodes never cost a card.
    } else {
      // Dropping below critical resets the clock — that's what makes a
      // Cooling Jacket or Flash Freeze an actual save.
      node.isOverheatWarning = false;
      node.overheatTicks = 0;
    }
  }
}

/** §2.2 Pressure — one counter per pipe that was blocked this tick. */
export function accumulatePressure(
  state: EngineState,
  blockedEdgeIds: readonly string[],
  events: SimEvent[],
): void {
  const blocked = new Set(blockedEdgeIds);
  const edges = [...state.edges].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const edge of edges) {
    if (edge.isBurst) continue;

    if (blocked.has(edge.id)) {
      edge.pressureCounter += 1;
      edge.isOverpressured = true;
      events.push({ type: "pipe.pressured", edgeId: edge.id, pressureCounter: edge.pressureCounter });
    } else {
      // Flow resumed: the line de-pressurises AND the counter resets, so only
      // strictly consecutive stalls build toward a burst — a stall, a
      // resumed push, then another stall starts over from 0.
      edge.isOverpressured = false;
      edge.pressureCounter = 0;
    }
  }
}

/** §2.4 Catalyst poisoning — charged on entry to a catalytic bed. */
export function accumulateCatalystPoisoning(state: EngineState, events: SimEvent[]): void {
  const nodes = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const node of nodes) {
    if (node.isDestroyed || node.kind !== "catalyticBed") continue;
    if (node.catalystHealth <= 0) continue;

    for (const packet of node.buffer) {
      if (packet.residenceTicks > 0) continue; // only charge newly arrived packets
      if (!isBelowTargetPurity(packet.composition, state.stage.objective.targetComposition, CATALYST_MIN_PURITY)) {
        continue;
      }

      // §2.4's mitigation: a guard bed / polishing filter upstream takes the
      // hit instead, until its charges are used up.
      if (node.guardCharges > 0) {
        node.guardCharges -= 1;
        continue;
      }

      node.catalystHealth = Math.max(0, node.catalystHealth - CATALYST_POISON_PER_IMPURE);
      events.push({ type: "catalyst.poisoned", nodeId: node.id, catalystHealth: node.catalystHealth });
      if (node.catalystHealth <= 0) {
        pollute(state, "catalystDead", node.id, events);
        break;
      }
    }
  }
}

/**
 * Q9 (d): only the six processing categories turn residence into sludge —
 * `inputTank`, `productHopper` and `wasteBuyer` are pass-through I/O
 * (`CATEGORY_OF_NODE_KIND` maps all three to `null`), and `flowMover`/`flowBuffer` are conduits, not
 * stagnant vessels, despite sharing a buffer of their own
 * (`flowMoverBuffer`). This is narrower than "parks a packet at all" — §3.3's
 * vermin infestation charges on the same `residenceTicks` field for every
 * non-hopper kind, sludge exemption included, so `accumulateResidence` still
 * counts residence for them; it just never converts it.
 */
export function isProcessingNode(node: EngineNode): boolean {
  const category = CATEGORY_OF_NODE_KIND[node.kind];
  return category !== null && category !== "flowMoverBuffer";
}

/** §2.5 Residence — a packet parked too long turns to sludge. */
export function accumulateResidence(state: EngineState, events: SimEvent[]): void {
  const nodes = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const node of nodes) {
    if (node.isDestroyed) continue;
    // A hopper (or a vent, spec 0032) is a destination, not a stagnant buffer.
    if (node.kind === "productHopper" || node.kind === "wasteBuyer" || node.kind === "vent") continue;

    for (const packet of node.buffer) {
      packet.residenceTicks += 1;
      if (node.kind === "incubator") {
        const holdRecipe = resolveHoldRecipeForPacket(state, node, packet);
        if (
          holdRecipe !== undefined &&
          (holdRecipe.minTemperature === undefined || packet.temperature >= holdRecipe.minTemperature) &&
          node.verminActiveThisTick
        ) {
          packet.holdTicks += 1;
        }
      }
      if (
        isProcessingNode(node) &&
        packet.residenceTicks >= SLUDGE_RESIDENCE_TICKS &&
        !packet.hasResidenceSludged
      ) {
        packet.hasResidenceSludged = true;
        // Q9 (c): the dwell converts 1 unit of the packet's own reagent into
        // Sludge — a real product, not just a flag — so purity, scoring and
        // every downstream catalyst check see it.
        packet.composition = convertUnitToSludge(packet.composition);
        // 03-hazards.md §2.5: decomposition "sets isContaminated = true" —
        // literal even when the packet was already contaminated on arrival.
        packet.isContaminated = true;
        events.push({ type: "packet.sludged", packetId: packet.id, nodeId: node.id });
        pollute(state, "sludge", node.id, events);
      }
    }
  }
}

/**
 * §3.1 grid contamination — an unlined edge with a waypoint orthogonally
 * adjacent to a contaminated tile loses CONTAMINATION_HP_PER_TICK, charged
 * once per edge per tick regardless of how many of its waypoints qualify.
 * Mirrors `applyCorrosion`'s hp-to-zero → leaking transition exactly
 * (movement.ts can't be imported from here without a cycle, so the shape is
 * duplicated on purpose — same semantics, not shared code).
 */
export function accumulateContamination(state: EngineState, events: SimEvent[]): void {
  if (state.contaminatedTiles.length === 0) return; // no allocation when nothing is contaminated

  const contaminatedKeys = new Set(state.contaminatedTiles.map((t) => `${t.pos.x},${t.pos.y}`));
  const edges = [...state.edges].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const edge of edges) {
    if (edge.isBurst || resistsCorrosion(edge) || edge.hp <= 0) continue;

    const isAdjacent = edge.path.some((waypoint) => {
      const neighbours = [
        `${waypoint.x - 1},${waypoint.y}`,
        `${waypoint.x + 1},${waypoint.y}`,
        `${waypoint.x},${waypoint.y - 1}`,
        `${waypoint.x},${waypoint.y + 1}`,
      ];
      return neighbours.some((key) => contaminatedKeys.has(key));
    });
    if (!isAdjacent) continue;

    edge.hp = Math.max(0, edge.hp - CONTAMINATION_HP_PER_TICK);
    events.push({ type: "pipe.corroded", edgeId: edge.id, hp: edge.hp });

    if (edge.hp === 0 && !edge.isLeaking) {
      edge.isLeaking = true;
      events.push({ type: "pipe.leaking", edgeId: edge.id });
      pollute(state, "leak", edge.id, events);
    }
  }
}

/**
 * §3.3 pest infestation — a packet that hits exactly VERMIN_STAGNANT_TICKS of
 * residence raises the facility-wide verminRisk counter by 1. Exact equality,
 * not >=, is what makes this "+1 per occurrence" without a new packet field:
 * residenceTicks resets to 0 on any move, so a packet can only re-cross the
 * threshold after another VERMIN_STAGNANT_TICKS stagnant ticks. No `pollute()`
 * here — that only happens once a node is actually disabled, in
 * `resolveFailures`, on the "vermin" reason.
 */
export function accumulateVermin(state: EngineState, events: SimEvent[]): void {
  const nodes = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const node of nodes) {
    if (node.isDestroyed) continue;
    // A hopper (or a vent, spec 0032, OQ2 (a)) is a destination, not a stagnant buffer.
    if (node.kind === "productHopper" || node.kind === "wasteBuyer" || node.kind === "vent") continue;

    for (const packet of node.buffer) {
      if (packet.residenceTicks !== VERMIN_STAGNANT_TICKS) continue;
      accrueVerminRisk(state, node.id, events);
    }
  }
}

/**
 * Failure resolution: turns crossed thresholds into destroyed hardware.
 * Runs after every counter has been updated for the tick.
 */
export function resolveFailures(state: EngineState, events: SimEvent[]): void {
  // §2.1 — explode after OVERHEAT_TICKS_TO_EXPLOSION uncooled ticks.
  const explodingCandidates = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const node of explodingCandidates) {
    if (node.isDestroyed || !node.isOverheatWarning) continue;
    if (node.overheatTicks < OVERHEAT_TICKS_TO_EXPLOSION) continue;

    node.isDestroyed = true;
    node.isOverheatWarning = false;
    // Thermal Shock lands on the explosion tick, not the warning tick that
    // preceded it — a node cooled in time never injected anything.
    pollute(state, "overheat", node.id, events);

    // "spilling resident reagents" destroys whatever the node and its pipes
    // were holding — tallied as waste before the buffers are cleared.
    const touching = edgesTouching(state, node.id);
    let destroyedUnits = node.buffer.reduce((sum, packet) => sum + totalUnits(packet.composition), 0);
    for (const edge of touching) {
      for (const transit of edge.transit) destroyedUnits += totalUnits(transit.packet.composition);
    }
    state.wastedUnits += destroyedUnits;

    node.buffer = [];

    // "destroying itself and clearing adjacent pipe connections"
    for (const edge of touching) {
      edge.isBurst = true;
      edge.transit = [];
    }

    events.push({
      type: "node.exploded",
      nodeId: node.id,
      clearedEdgeIds: touching.map((e) => e.id),
    });

    // "spilling resident reagents onto surrounding grid tiles" (§2.1) —
    // resolved (open question 1) to the node's own tile plus its 4 orthogonal
    // neighbours, self first then a fixed order for determinism.
    const candidates: GridPos[] = [
      { x: node.pos.x, y: node.pos.y },
      { x: node.pos.x - 1, y: node.pos.y },
      { x: node.pos.x + 1, y: node.pos.y },
      { x: node.pos.x, y: node.pos.y - 1 },
      { x: node.pos.x, y: node.pos.y + 1 },
    ];
    for (const pos of candidates) {
      if (pos.x < 0 || pos.x >= state.stage.cols || pos.y < 0 || pos.y >= state.stage.rows) continue;
      contaminateTile(state, pos, "explosion", node.id, events);
    }
  }

  // §2.2 — burst once the counter reaches threshold (plus any burst-disk relief).
  const burstCandidates = [...state.edges].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const edge of burstCandidates) {
    if (edge.isBurst) continue;

    const target = state.nodes.find((n) => n.id === edge.toNodeId);
    const tolerance = target ? pressureToleranceOf(target) : 0;
    if (edge.pressureCounter < PRESSURE_BURST_THRESHOLD + tolerance) continue;

    edge.isBurst = true;
    edge.isOverpressured = false;

    // "spilling the fluid packet onto the grid tile" — the spill point is
    // wherever the head packet had reached.
    const head = [...edge.transit].sort((a, b) => b.pathIndex - a.pathIndex)[0];
    const spillAt = head ? edge.path[head.pathIndex]! : edge.path[edge.path.length - 1]!;
    edge.transit = [];

    events.push({ type: "pipe.burst", edgeId: edge.id, spillAt: { ...spillAt } });
    contaminateTile(state, spillAt, "burst", edge.id, events);
    // §4's Line Over-Pressure row: a burst never injected a card of its own
    // before spec 0007 — the missing pollute() call the Mechanism section
    // names. `contaminateTile` above already polluted under "spill"; this is
    // the burst's own reason, keyed per edge like the leak path.
    pollute(state, "burst", edge.id, events);
  }

  // §3.3 — rodents move into one live, not-already-infested infestable node
  // once verminRisk crosses the threshold (spec 0033, replacing the old
  // flowMover-only setting lock). Overflow above the threshold carries
  // forward (open question 7): the counter decrements by the threshold, not
  // to 0, so a high spike can infest a second node on a later tick without
  // re-accumulating from scratch.
  if (state.verminRisk >= VERMIN_RISK_THRESHOLD) {
    const isEligible = (n: EngineNode): boolean =>
      !n.isDestroyed &&
      CATEGORY_OF_NODE_KIND[n.kind] !== null &&
      n.infestation === null &&
      !n.buffer.some((p) => p.hasResidenceSludged);

    // Tier 1: a node currently holding a packet. Tier 2 (fallback, decision
    // 3): any other eligible node, holding one or not. Both sorted by id
    // before the draw, so the RNG only ever picks over a stable order.
    const holding = state.nodes
      .filter((n) => isEligible(n) && n.buffer.length >= 1)
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const fallback = state.nodes.filter(isEligible).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

    const candidates = holding.length > 0 ? holding : fallback;

    if (candidates.length > 0) {
      const chosen = candidates[state.rng.nextInt(candidates.length)]!;
      // Spec 0028: Facility Mousetrap widens the lost-tick period to 1 in 4
      // instead of 1 in 2 (decision 6) — it keeps its halved verminRisk
      // accrual. `verminLostTickPeriodOf` is read live, not stored, so this
      // event's payload always agrees with what `resolveInfestationGate`
      // and `projectNode` compute for the same node later.
      const lostTickPeriod = verminLostTickPeriodOf(state);
      chosen.infestation = { sinceTurn: state.turn, ticksInfested: 0, heldPacketThisTurn: false };
      state.verminRisk -= VERMIN_RISK_THRESHOLD;
      events.push({ type: "node.infested", nodeId: chosen.id, lostTickPeriod });
      pollute(state, "vermin", chosen.id, events);
    }
    // With no live, not-already-infested infestable node, nothing fires and
    // the RNG is not consulted — verminRisk only decrements when a candidate
    // exists.
  }
}

/**
 * The rig is dead when DAMAGE has severed every route from an input tank to
 * the hopper. No design section states this rule today — the closest is
 * `00-pillars.md` §2's Legible Causality, and what counts as a fatal severance
 * once there is more than one hopper kind is still open.
 *
 * The damage precondition matters: without it, any stage that simply has no
 * completed route yet (a fresh board the player is still building on, or a
 * focused test rig) would report failure on its very first tick.
 */
export function isRigDead(state: EngineState): boolean {
  const hasDamage =
    state.nodes.some((n) => n.isDestroyed) || state.edges.some((e) => e.isBurst);
  if (!hasDamage) return false;

  // G-B5: check every live productHopper, not just the first — a rig with a
  // healthy objective path is not dead just because a separate wasteBuyer (or
  // a second hopper) got severed. wasteBuyer never factors in here at all: a
  // dead waste line isn't a rig-ending problem, only the objective path is.
  const hoppers = state.nodes.filter((n) => n.kind === "productHopper" && !n.isDestroyed);
  if (hoppers.length === 0) return true;

  const inputs = state.nodes.filter((n) => n.kind === "inputTank" && !n.isDestroyed);
  if (inputs.length === 0) return true;

  return !inputs.some((input) => hoppers.some((hopper) => hasRoute(state, input.id, hopper.id)));
}

function hasRoute(state: EngineState, fromNodeId: string, toNodeId: string): boolean {
  const seen = new Set<string>([fromNodeId]);
  const queue = [fromNodeId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === toNodeId) return true;

    for (const edge of state.edges) {
      if (edge.fromNodeId !== current || edge.isBurst) continue;
      const next = state.nodes.find((n) => n.id === edge.toNodeId);
      if (!next || next.isDestroyed || seen.has(next.id)) continue;
      seen.add(next.id);
      queue.push(next.id);
    }
  }

  return false;
}
